import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HiOutlineChevronDown } from 'react-icons/hi2';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../supabaseClient';
import { SUPPORTED_LANGUAGES } from '../i18n';
import './Header.css';
import SafeAvatar from './SafeAvatar';
import AvatarEditPopup, { deriveAvatarInitial } from './AvatarEditPopup';

const THEME_STORAGE_KEY = 'lansly-theme';

function readThemeFromDom() {
  if (typeof document === 'undefined') return 'dark';
  const attr = document.documentElement.getAttribute('data-theme');
  if (attr === 'dark' || attr === 'light') return attr;
  return 'dark';
}

function ThemeMoonIcon() {
  return (
    <svg
      className="theme-toggle-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function ThemeSunIcon() {
  return (
    <svg
      className="theme-toggle-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}

function Header({ onMenuClick, showPopup, onClosePopup, onLoginClick }) {
  const { t, i18n } = useTranslation();
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showNameEditModal, setShowNameEditModal] = useState(false);
  const [showAvatarEditModal, setShowAvatarEditModal] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [nameError, setNameError] = useState('');
  const [theme, setTheme] = useState(readThemeFromDom);
  const activeLang =
    SUPPORTED_LANGUAGES.find((l) => l.code === i18n.resolvedLanguage) || SUPPORTED_LANGUAGES[0];
  const langDropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const headerNavRef = useRef(null);
  const hamburgerRef = useRef(null);
  const { user, profile, signOut, refreshProfile } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e) => {
      const target = e.target;
      const isLangDropdownClicked = langDropdownRef.current?.contains(target);
      const isProfileDropdownClicked = profileDropdownRef.current?.contains(target);

      if (!isLangDropdownClicked) {
        setShowLangDropdown(false);
      }

      if (!isProfileDropdownClicked) {
        setShowProfileDropdown(false);
      }

      if (isMenuOpen) {
        const inNav = headerNavRef.current?.contains(target);
        const inHamburger = hamburgerRef.current?.contains(target);
        if (!inNav && !inHamburger) {
          setIsMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch (_) {}
  }, [theme]);

  const toggleTheme = () => {
    setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
  };

  const handleLangSelect = (code) => {
    i18n.changeLanguage(code);
    setShowLangDropdown(false);
  };

  const handleLogout = async () => {
    if (!window.confirm(t('header.logoutConfirm'))) return;
    await signOut();
    setShowProfileDropdown(false);
    setIsMenuOpen(false);
    navigate('/');
  };

  const handleDashboardClick = () => {
    navigate('/dashboard');
    setShowProfileDropdown(false);
    setIsMenuOpen(false);
  };

  const handleSubscriptionClick = () => {
    navigate('/profile');
    setShowProfileDropdown(false);
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const displayName = profile?.updated_display_name;
  const avatarUrl = profile?.updated_avatar_url;
  // updated_avatar_url 이 비어있으면 무조건 닉네임 첫 글자.
  // (OAuth user_metadata.avatar_url 로 fallback 안 함 — 사용자가 지운 게 다시 보이는 문제 방지)
  // 프로필이 아직 로드 안 돼 닉네임도 없을 땐 '?' 대신 SafeAvatar 의 PersonIcon 으로 떨어지게 null.
  const avatarSrcForRender = (() => {
    if (typeof avatarUrl === 'string' && avatarUrl.trim()) return avatarUrl;
    const initial = deriveAvatarInitial(displayName);
    return initial && initial !== '?' ? initial : null;
  })();

  const handleNameUpdate = async (e) => {
    e.preventDefault();
    setNameError('');

    const trimmedName = newName.trim();
    if (!trimmedName) {
      setNameError(t('header.nameRequired'));
      return;
    }

    if (trimmedName === displayName) {
      setNameError(t('header.nameSame'));
      return;
    }

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          updated_display_name: trimmedName,
          updated_at: new Date(),
        })
        .eq('id', user.id);

      if (error) throw error;

      await refreshProfile();
      setShowNameEditModal(false);
      setNewName('');
      window.location.reload();
    } catch (error) {
      console.error('이름 수정 에러:', error.message);
      alert(t('header.nameUpdateError'));
    }
  };

  return (
    <>
      <header className="main-header">
        <div className="container">
          <div className="header-content">
            <div className="header-logo">
              <Link
                to="/"
                style={{ textDecoration: 'none' }}
                onClick={() => {
                  window.scrollTo(0, 0);
                  setIsMenuOpen(false);
                }}
              >
                <h1>
                  Lansly<span className="logo-dot">.</span>
                </h1>
              </Link>
            </div>
            <nav ref={headerNavRef} className={`header-nav ${isMenuOpen ? 'mobile-open' : ''}`}>
              {/* <button className="nav-link" onClick={handleNavLinkClick}>외주 찾기</button>
              <button className="nav-link" onClick={handleNavLinkClick}>전문가 찾기</button>
              <button className="nav-link" onClick={handleNavLinkClick}>챌린지 찾기</button> */}
              <button className="nav-link" onClick={() => navigate('/community')}>
                {t('header.community')}
              </button>
            </nav>
            <div className="header-actions">
              <button
                ref={hamburgerRef}
                type="button"
                className={`hamburger ${isMenuOpen ? 'is-active' : ''}`}
                onClick={toggleMenu}
                aria-label={t('header.menu')}
                aria-expanded={isMenuOpen}
              >
                <span className="hamburger-box">
                  <span className="hamburger-inner"></span>
                </span>
              </button>
              <div className="lang-dropdown-wrapper" ref={langDropdownRef}>
                <button
                  type="button"
                  className="lang-btn"
                  aria-label={t('header.language')}
                  aria-expanded={showLangDropdown}
                  aria-haspopup="listbox"
                  onClick={() => setShowLangDropdown(!showLangDropdown)}
                >
                  <span className="lang-btn-code">{activeLang.short}</span>
                  <HiOutlineChevronDown
                    className={`lang-arrow${showLangDropdown ? ' lang-arrow-open' : ''}`}
                    aria-hidden
                  />
                </button>
                {showLangDropdown && (
                  <div className="lang-dropdown" role="listbox">
                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        role="option"
                        aria-selected={lang.code === activeLang.code}
                        className={`lang-option${lang.code === activeLang.code ? ' lang-option-active' : ''}`}
                        onClick={() => handleLangSelect(lang.code)}
                      >
                        <span className="lang-option-code" aria-hidden>
                          {lang.short}
                        </span>
                        {lang.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="button"
                className="theme-toggle-btn"
                onClick={toggleTheme}
                aria-pressed={theme === 'dark'}
                aria-label={theme === 'dark' ? t('header.toLightMode') : t('header.toDarkMode')}
                title={theme === 'dark' ? t('header.lightMode') : t('header.darkMode')}
              >
                {theme === 'dark' ? <ThemeMoonIcon /> : <ThemeSunIcon />}
              </button>

              {user ? (
                <div className="profile-dropdown-wrapper" ref={profileDropdownRef}>
                  <button
                    className="profile-btn"
                    onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  >
                    <SafeAvatar
                      src={avatarSrcForRender}
                      alt="Profile"
                      className="profile-img"
                      fallbackClassName="profile-img"
                    />
                  </button>
                  {showProfileDropdown && (
                    <div className="profile-dropdown">
                      <div className="profile-info">
                        <button
                          type="button"
                          className="profile-info-img-btn"
                          onClick={() => {
                            setShowAvatarEditModal(true);
                            setShowProfileDropdown(false);
                          }}
                          aria-label={t('header.editThumbnail')}
                          title={t('header.editThumbnail')}
                        >
                          <SafeAvatar
                            src={avatarSrcForRender}
                            alt="Profile"
                            className="profile-info-img"
                            fallbackClassName="profile-info-img"
                          />
                        </button>
                        <div className="profile-text">
                          <div className="profile-name-wrapper">
                            <p className="profile-name">{displayName}</p>
                            <button
                              type="button"
                              className="name-edit-btn"
                              onClick={() => {
                                setNewName(displayName);
                                setShowNameEditModal(true);
                                setShowProfileDropdown(false);
                              }}
                            >
                              ✎
                            </button>
                          </div>
                          <p className="profile-email">{user.email}</p>
                        </div>
                      </div>
                      <div className="profile-menu">
                        <button
                          className="profile-menu-item dashboard-btn"
                          onClick={handleDashboardClick}
                        >
                          {t('header.dashboard')}
                        </button>
                        <button
                          className="profile-menu-item subscription-btn"
                          onClick={handleSubscriptionClick}
                        >
                          {t('header.profile')}
                        </button>
                        <button className="profile-menu-item logout-btn" onClick={handleLogout}>
                          {t('header.logout')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button className="login-btn" onClick={onLoginClick}>
                  {t('common.login')}
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {showPopup && (
        <div className="popup-overlay" onClick={onClosePopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close" onClick={onClosePopup} aria-label={t('header.closePopup')}>
              ×
            </button>
            <p>{t('header.comingSoon')}</p>
          </div>
        </div>
      )}

      {showNameEditModal && (
        <div
          className="name-edit-overlay"
          onClick={() => {
            setShowNameEditModal(false);
            setNameError('');
          }}
        >
          <div
            className="name-edit-modal name-edit-modal--small"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="name-edit-close"
              onClick={() => {
                setShowNameEditModal(false);
                setNameError('');
              }}
              aria-label={t('header.close')}
            >
              ×
            </button>
            <h2 className="name-edit-title">{t('header.nameEditTitle')}</h2>
            <form onSubmit={handleNameUpdate}>
              <div className="name-edit-form-group">
                <input
                  type="text"
                  className={`name-edit-input${nameError ? ' name-edit-input-error' : ''}`}
                  value={newName}
                  onChange={(e) => {
                    setNewName(e.target.value);
                    if (nameError) setNameError('');
                  }}
                  onFocus={(e) => {
                    // 모바일 삼성 브라우저: 키보드 올라온 직후 포커스 input 을 가운데로 스크롤.
                    // fixed 모달이라 scrollIntoView 가 무효한 경우 모달 자체를 위로 끌어올림.
                    const el = e.target;
                    setTimeout(() => {
                      try {
                        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
                      } catch {}
                      requestAnimationFrame(() => {
                        try {
                          const vp = window.visualViewport;
                          if (!vp) return;
                          const modal = el.closest('.name-edit-modal');
                          if (!modal) return;
                          const inputRect = el.getBoundingClientRect();
                          const visibleBottom = vp.offsetTop + vp.height;
                          const overflow = inputRect.bottom - visibleBottom;
                          if (overflow > 0) {
                            const shift = overflow + 16;
                            modal.style.transition = 'transform 0.25s ease';
                            modal.style.transform = `translateY(-${shift}px)`;
                            const restore = () => {
                              modal.style.transform = '';
                              el.removeEventListener('blur', restore);
                            };
                            el.addEventListener('blur', restore);
                          }
                        } catch {}
                      });
                    }, 300);
                  }}
                  placeholder={t('header.namePlaceholder')}
                />
                {nameError && <div className="name-edit-error">{nameError}</div>}
              </div>
              <div className="name-edit-actions">
                <button
                  type="button"
                  className="name-edit-btn-outline"
                  onClick={() => {
                    setShowNameEditModal(false);
                    setNameError('');
                  }}
                >
                  {t('header.cancel')}
                </button>
                <button type="submit" className="name-edit-btn-primary">
                  {t('header.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAvatarEditModal && (
        <AvatarEditPopup
          user={user}
          currentAvatarUrl={avatarUrl}
          displayName={displayName}
          onClose={() => setShowAvatarEditModal(false)}
          onSaved={() => {
            // 저장/삭제 완료 후 — 새 썸네일/fallback 으로 화면 새로고침
            window.location.reload();
          }}
        />
      )}
    </>
  );
}

export default Header;
